import { Drawer as DrawerMUI } from '@mui/material';
import Box from '@mui/material/Box';
import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import IconButton from '@mui/material/IconButton';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useNavigate } from '@tanstack/react-router';
import Logo from '@/common/Logo';
import PropTypes from 'prop-types';
import useStore, {
  selectCountryTheme,
  selectFavoriteCountryId,
  selectContact,
  selectSiteUrl,
  selectBrand,
} from '@/hooks/client/useStore';

const css = {
  contentBox: (color, drawerWidth) => ({
    width: drawerWidth,
    height: '100vh',
    backgroundColor: color,
    display: 'flex',
    flexDirection: 'column',
  }),
  topBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 24px',
  },
  closeIcon: (color) => ({ color: color, fontSize: '48px' }),
  navBox: {
    flexGrow: 1,
    paddingTop: 2,
    paddingX: 2,
  },
  navItems: {
    color: '#fff',
    justifyContent: 'flex-start',
    textAlign: 'left',
    paddingY: 2,
    textTransform: 'none',
    fontSize: '1.1rem',
    width: '100%',
    borderBottom: '1.8px solid white ',
    borderRadius: '0px',
  },
  siteLink: {
    color: '#fff',
    textTransform: 'uppercase',
    fontWeight: '800',
    textDecoration: 'none',
  },
  dataPolicyLink: { textDecoration: 'underline', color: '#fff', mt: 3, fontSize: '1.1rem' },
};

const pages = (contactUrl) => [
  { page: 'Inicio', url: '/', externalSite: false, phone: null },
  { page: 'Conoce nuestras mejores recetas', url: '/recipes', externalSite: false, phone: null },
  {
    page: 'Contáctanos',
    url: contactUrl.contactInfo,
    externalSite: true,
    phone: contactUrl.contactMethod !== 'url' ? contactUrl.contactInfo : null,
  },
];

// Drawer content with logo and close button
const Drawer = ({ setDrawerOpen, drawerOpen }) => {
  const navigate = useNavigate();
  const countryTheme = useStore(selectCountryTheme);
  const countryCode = useStore(selectFavoriteCountryId);
  const contact = useStore(selectContact);
  const siteUrl = useStore(selectSiteUrl);
  const isSmallScreen = useMediaQuery('(max-width:600px)');
  const isLandscape = useMediaQuery('(orientation:landscape)');
  const drawerWidthSm = isSmallScreen ? '90vw' : '33.33vw';
  const drawerWidth = isLandscape ? '50vw' : drawerWidthSm;
  const brand = useStore(selectBrand);

  const handleNavigate = (url, externalSite, phone) => {
    if (phone) return;
    if (externalSite) {
      window.open(url, '_blank');
    } else {
      setDrawerOpen(false);
      navigate({ to: url });
    }
  };

  const toggleDrawer = (open) => () => {
    setDrawerOpen(open);
  };

  return (
    <DrawerMUI
      anchor="right"
      open={drawerOpen}
      onClose={toggleDrawer(false)}
      PaperProps={{
        sx: {
          width: drawerWidth,
        },
      }}
    >
      <Box sx={css.contentBox(countryTheme.navColor, drawerWidth)} role="dialog">
        {/* Top bar inside drawer with logo and close icon */}
        <Box sx={css.topBar}>
          <Logo sx={{ width: countryTheme.logoMinWidth, height: countryTheme.logoMinHeigth }} />
          <IconButton onClick={toggleDrawer(false)}>
            <CloseIcon sx={css.closeIcon(countryTheme.accentColor)} />
          </IconButton>
        </Box>
        {/* Navigation buttons */}
        <Box sx={css.navBox}>
          {pages(contact).map((p) => (
            <Button
              key={p.page}
              aria-label={p.page}
              sx={css.navItems}
              onClick={() => handleNavigate(p.url, p.externalSite, p.phone)}
              href={p.phone && `tel:${p.phone}`}
            >
              {p.page}
            </Button>
          ))}
        </Box>

        {/* Data policy and site url */}
        <Box
          sx={{ minHeight: '15vh', textAlign: 'center', display: 'flex', flexDirection: 'column' }}
        >
          <Link
            variant="subtitle1"
            href={siteUrl}
            target="_blank"
            rel="noopener noreferrer" // prevents the new tab from accessing window.opener
            sx={css.siteLink}
          >
            {brand.replace(' ', '')}.com
          </Link>
          <Link
            variant="body2"
            href={`/docs/${countryCode}/data-policy.pdf`}
            rel="noopener noreferrer"
            sx={css.dataPolicyLink}
          >
            Politica de tratamiento de datos
          </Link>
        </Box>
      </Box>
    </DrawerMUI>
  );
};

Drawer.propTypes = {
  setDrawerOpen: PropTypes.func.isRequired,
  drawerOpen: PropTypes.bool.isRequired,
};

export default Drawer;
